import { and, type Collection, eq, gte, type InitialQueryBuilder, inArray, isNull, or, type Ref } from "@tanstack/db";

import { languageFamily, type PreferredLanguage } from "@/models/languages";
import { compareDesc } from "@/store/compare";
import { idx, rowsByIndex, store } from "@/store/store";
import type {
  Account,
  AccountEvent,
  AccountWithWsMembershipAvailability,
  BroadcastAction,
  BroadcastRecipient,
  DisplayArtifact,
  File,
  MembershipAvailability,
  ScheduleTrigger,
  Transcription,
  WorkflowItem,
  WorkspaceCommandAlias,
  WorkspaceMembership,
} from "@/store/types";

/**
 * Window predicate shared by every feed-content live query below: non-deleted items of the feed
 * inside the pagination window (`createdAt >= windowStartTimestamp`) — see `FeedContextProvider`
 * and WEB-1985.
 */
function inWindow(
  item: { feedId: unknown; deletedAt: unknown; createdAt: unknown },
  currentFeedId: string,
  windowStartTimestamp?: string,
) {
  const scoped = and(eq(item.feedId, currentFeedId), isNull(item.deletedAt));
  return windowStartTimestamp ? and(scoped, gte(item.createdAt, windowStartTimestamp)) : scoped;
}

/** The window's items, oldest first. */
export const itemsQuery = (currentFeedId: string, windowStartTimestamp?: string) => (q: InitialQueryBuilder) =>
  q
    .from({ item: store.collection("item") })
    .where(({ item }) => inWindow(item, currentFeedId, windowStartTimestamp))
    .orderBy(({ item }) => item.createdAt, { direction: "asc", nulls: "last" });

/**
 * Builds a live query for one artifact collection inner-joined to the window's items via
 * contentId. An artifact shared by two items is returned once — the downstream contentId-keyed
 * maps don't dedupe (WEB-2149), so the query ends in `distinct()` over the selected artifact row.
 *
 * Drives from `item` (the side carrying the non-indexable window residuals) and joins the
 * artifact side lazily: the artifact subscription then has no residual predicate, so TanStack
 * answers its per-commit `in(contentId, …)` join-key loads from the index alone instead of
 * re-scanning the collection per key batch.
 */
function artifactsForWindowQuery<T extends { contentId: string }>(
  collection: Collection<T, string>,
  currentFeedId: string,
  windowStartTimestamp?: string,
) {
  return (q: InitialQueryBuilder) =>
    q
      .from({ item: store.collection("item") })
      .innerJoin({ artifact: collection }, ({ item, artifact }) => eq(artifact.contentId, item.contentId))
      .where(({ item }) => inWindow(item, currentFeedId, windowStartTimestamp))
      // TanStack DB 0.12 types a whole-source select as `T | { [K]?: never }`, even on an inner-join side. A return
      // annotation fixes that elsewhere, but it cannot resolve against the open `T` here, so this select casts.
      .select(({ artifact }) => artifact as Ref<T>)
      .distinct();
}

/**
 * The window's transcriptions a reader of `selectedLanguage` can be shown: every dialect of the language, since a
 * message carries one of them (`es` or `es-MX`), and the originals, which `selectTranscription` falls back to when the
 * message has no translation for the reader. `selectTranscription` ranks what this returns.
 */
export const transcriptionsQuery =
  (currentFeedId: string, selectedLanguage: string, windowStartTimestamp?: string) => (q: InitialQueryBuilder) =>
    q
      .from({ item: store.collection("item") })
      .innerJoin({ transcription: store.collection("transcription") }, ({ item, transcription }) =>
        eq(transcription.contentId, item.contentId),
      )
      .where(({ transcription, item }) =>
        and(
          or(
            inArray(transcription.language, languageFamily(selectedLanguage as PreferredLanguage)),
            isNull(transcription.translatedFrom),
            eq(transcription.translatedFrom, ""),
          ),
          inWindow(item, currentFeedId, windowStartTimestamp),
        ),
      )
      // TanStack DB 0.12 types a whole-source select as `T | { [K]?: never }`; the annotation keeps the row type `T`.
      .select(({ transcription }): Ref<Transcription> => transcription)
      .distinct();

export const audioEncodingsQuery = (currentFeedId: string, windowStartTimestamp?: string) =>
  artifactsForWindowQuery(store.collection("audioEncoding"), currentFeedId, windowStartTimestamp);

export const displayArtifactsQuery = (currentFeedId: string, windowStartTimestamp?: string) =>
  artifactsForWindowQuery(store.collection("displayArtifact"), currentFeedId, windowStartTimestamp);

export const callRecordsQuery = (currentFeedId: string, windowStartTimestamp?: string) =>
  artifactsForWindowQuery(store.collection("callRecord"), currentFeedId, windowStartTimestamp);

export const filesQuery = (currentFeedId: string, windowStartTimestamp?: string) =>
  artifactsForWindowQuery(store.collection("file"), currentFeedId, windowStartTimestamp);

export const linksQuery = (currentFeedId: string, windowStartTimestamp?: string) =>
  artifactsForWindowQuery(store.collection("link"), currentFeedId, windowStartTimestamp);

/** Account events attached to the window's items, deduped like the artifact queries. */
export const eventsForFeedItemsQuery =
  (currentFeedId: string, windowStartTimestamp?: string) => (q: InitialQueryBuilder) =>
    q
      .from({ item: store.collection("item") })
      .innerJoin({ event: store.collection("accountEvent") }, ({ item, event }) => eq(event.itemId, item.id))
      .where(({ item }) => inWindow(item, currentFeedId, windowStartTimestamp))
      // TanStack DB 0.12 types a whole-source select as `T | { [K]?: never }`; the annotation keeps the row type `T`.
      .select(({ event }): Ref<AccountEvent> => event)
      .distinct();

/**
 * Reactive probe used by FeedContextProvider to derive a channel's window anchor: the createdAt
 * of the (pageCount * pageSize)th newest non-deleted item in the feed. Returns no rows once the
 * window covers the channel's full local history.
 */
export const feedWindowAnchorQuery = (currentFeedId: string, offset: number) => (q: InitialQueryBuilder) =>
  q
    .from({ item: store.collection("item") })
    .where(({ item }) => and(eq(item.feedId, currentFeedId), isNull(item.deletedAt)))
    .orderBy(({ item }) => item.createdAt, { direction: "desc", nulls: "first" })
    .offset(offset)
    .limit(1)
    .select(({ item }) => ({ id: item.id, createdAt: item.createdAt }));

export const workspaceAvailabilityQuery = (workspaceId: string) => (q: InitialQueryBuilder) =>
  q
    .from({ availability: store.collection("wsMembershipAvailability") })
    .where(({ availability }) => eq(availability.workspaceId, workspaceId));

export const workspacePermissionsQuery = (workspaceId: string) => (q: InitialQueryBuilder) =>
  q
    .from({ permission: store.collection("permission") })
    .innerJoin({ membership: store.collection("workspaceMembership") }, ({ permission, membership }) =>
      eq(permission.workspaceMembershipId, membership.id),
    )
    .where(({ membership }) => eq(membership.workspaceId, workspaceId))
    .select(({ permission }) => ({ id: permission.id }));

/** The identity collections {@link accountStatusOf} reads; availability is deliberately absent. */
export const accountLookupCollections = ["account", "workspaceMembership", "workspaceCommandAlias"] as const;

/** Every collection {@link accountStatusOf} reads. */
export const workspaceAccountsWithStatusCollections = [
  ...accountLookupCollections,
  "wsMembershipAvailability",
] as const;

/**
 * One account's row in a workspace, merging the account with its membership, availability and alias.
 *
 * Keeps the last membership the account holds in the workspace and the last alias with a feed, matching the
 * whole-workspace map this replaces, which kept the last row per account id.
 *
 * @param workspaceId - The workspace the membership belongs to.
 * @param accountId - The account to read.
 * @returns The merged row, or null when the account or its membership in the workspace is missing.
 */
export function accountStatusOf(workspaceId: string, accountId: string): AccountWithWsMembershipAvailability | null {
  const inputs = accountStatusInputsOf(workspaceId, accountId);
  return inputs ? mergeAccountStatus(inputs) : null;
}

/** The rows {@link accountStatusOf} merges; their identities key the per-account snapshot cache. */
export type AccountStatusInputs = {
  account: Account;
  membership: WorkspaceMembership;
  availability: MembershipAvailability | undefined;
  alias: WorkspaceCommandAlias | undefined;
};

/**
 * Reads the rows {@link accountStatusOf} merges, without merging them.
 *
 * @param workspaceId - The workspace the membership belongs to.
 * @param accountId - The account to read.
 * @returns The input rows, or null when the account or its membership in the workspace is missing.
 */
export function accountStatusInputsOf(workspaceId: string, accountId: string): AccountStatusInputs | null {
  const account = store.get("account", accountId);
  if (!account) return null;
  const membership = rowsByIndex("workspaceMembership", idx.membershipsByAccount, accountId)
    .filter((row) => row.workspaceId === workspaceId)
    .at(-1);
  if (!membership) return null;
  const alias = rowsByIndex("workspaceCommandAlias", idx.commandAliasesByMembership, membership.id)
    .filter((row) => row.feedId != null)
    .at(-1);
  return { account, membership, availability: store.get("wsMembershipAvailability", membership.id), alias };
}

/**
 * Merges the rows {@link accountStatusInputsOf} read into one status row.
 *
 * @param inputs - The account, membership, availability and alias rows.
 * @returns The merged row; account fields win over availability fields.
 */
export function mergeAccountStatus({
  account,
  membership,
  availability,
  alias,
}: AccountStatusInputs): AccountWithWsMembershipAvailability {
  return {
    ...(availability ?? {}),
    ...account,
    alias: alias?.alias ?? null,
    role: membership.role,
    workspaceMembershipId: membership.id,
  } as AccountWithWsMembershipAvailability;
}

interface artifact {
  contentId: string;
}
export function mapForArtifact<T extends artifact>(rows: T[]) {
  const map = new Map<string, T[]>();
  for (const r of rows) {
    if (map.has(r.contentId)) {
      const t = map.get(r.contentId);
      t.push(r);
    } else {
      map.set(r.contentId, [r]);
    }
  }
  return map;
}
interface hasItemId {
  itemId: string;
}
export function mapForHasItemId<T extends hasItemId>(rows: T[]) {
  const map = new Map<string, T[]>();
  for (const r of rows) {
    if (map.has(r.itemId)) {
      const t = map.get(r.itemId);
      t.push(r);
    } else {
      map.set(r.itemId, [r]);
    }
  }
  return map;
}

interface hasWorkspaceMembershipId {
  workspaceMembershipId: string;
}
export function mapForHasWorkspaceMembershipIdMany<T extends hasWorkspaceMembershipId>(rows: T[] = []) {
  const map = new Map<string, T[]>();
  for (const r of rows) {
    if (map.has(r.workspaceMembershipId)) {
      const t = map.get(r.workspaceMembershipId);
      t.push(r);
    } else {
      map.set(r.workspaceMembershipId, [r]);
    }
  }
  return map;
}

export type WorkflowContentRow = {
  workflowItem: WorkflowItem;
  broadcastAction: BroadcastAction | null;
  scheduleTrigger: ScheduleTrigger | null;
  broadcastRecipient: BroadcastRecipient | null;
  transcription: Transcription | null;
  file: File | null;
  displayArtifact: DisplayArtifact | null;
};

export const workflowContentCollections = [
  "workflowItem",
  "broadcastAction",
  "scheduleTrigger",
  "broadcastRecipient",
  "transcription",
  "file",
  "displayArtifact",
] as const;

/**
 * Flat join of every workflow item with its schedule/recipient/content rows (grouped below).
 * Stays a compute pipeline: the SQL-style null-padded cartesian recreation is what the grouping
 * consumer expects, and the DSL's joins only take single-equality conditions.
 */
export function computeAllWorkflowContent(
  workspaceId: string,
  selectedLanguage: string,
  date: string,
): WorkflowContentRow[] {
  const workflowItems: WorkflowItem[] = [];
  for (const wi of store.getAll("workflowItem").values()) {
    if (wi.workspaceId === workspaceId && wi.deletedAt == null) workflowItems.push(wi);
  }
  workflowItems.sort((a, b) => compareDesc(a.createdAt, b.createdAt));

  const actionsByWorkflowItem = new Map<string, BroadcastAction[]>();
  for (const action of store.getAll("broadcastAction").values()) {
    const list = actionsByWorkflowItem.get(action.workflowItemId) ?? [];
    list.push(action);
    actionsByWorkflowItem.set(action.workflowItemId, list);
  }
  const triggersByAction = new Map<string, ScheduleTrigger[]>();
  for (const trigger of store.getAll("scheduleTrigger").values()) {
    if (trigger.broadcastActionId == null) continue;
    const list = triggersByAction.get(trigger.broadcastActionId) ?? [];
    list.push(trigger);
    triggersByAction.set(trigger.broadcastActionId, list);
  }
  const recipientsByBroadcast = new Map<string, BroadcastRecipient[]>();
  for (const recipient of store.getAll("broadcastRecipient").values()) {
    const list = recipientsByBroadcast.get(recipient.broadcastId) ?? [];
    list.push(recipient);
    recipientsByBroadcast.set(recipient.broadcastId, list);
  }

  const rows: WorkflowContentRow[] = [];
  for (const workflowItem of workflowItems) {
    const transcriptions = rowsByIndex("transcription", idx.transcriptionsByContent, workflowItem.contentId).filter(
      (t) => t.language === selectedLanguage,
    );
    const files = rowsByIndex("file", idx.filesByContent, workflowItem.contentId);
    const displayArtifacts = rowsByIndex("displayArtifact", idx.displayArtifactsByContent, workflowItem.contentId);
    const actions = actionsByWorkflowItem.get(workflowItem.id) ?? [];

    const actionRows: Array<{
      broadcastAction: BroadcastAction | null;
      scheduleTrigger: ScheduleTrigger | null;
      broadcastRecipient: BroadcastRecipient | null;
    }> = [];
    if (actions.length === 0) {
      actionRows.push({ broadcastAction: null, scheduleTrigger: null, broadcastRecipient: null });
    } else {
      for (const action of actions) {
        const triggers = triggersByAction.get(action.id) ?? [null];
        const recipients = recipientsByBroadcast.get(action.broadcastId) ?? [null];
        for (const trigger of triggers) {
          // Keep rows whose (left-joined) trigger has no one-time schedule or one still upcoming.
          if (trigger != null && trigger.oneTimeSchedule != null && trigger.oneTimeSchedule < date) {
            continue;
          }
          for (const recipient of recipients) {
            actionRows.push({ broadcastAction: action, scheduleTrigger: trigger, broadcastRecipient: recipient });
          }
        }
      }
      if (actionRows.length === 0) continue;
    }

    for (const actionRow of actionRows) {
      for (const transcription of transcriptions.length > 0 ? transcriptions : [null]) {
        for (const file of files.length > 0 ? files : [null]) {
          for (const displayArtifact of displayArtifacts.length > 0 ? displayArtifacts : [null]) {
            rows.push({ workflowItem, ...actionRow, transcription, file, displayArtifact });
          }
        }
      }
    }
  }
  return rows;
}

// Transform flat results from computeAllWorkflowContent into grouped structure
export function groupWorkflowContentResults(rows: WorkflowContentRow[]): {
  workflowItems: WorkflowItem[];
  broadcastActions: BroadcastAction[];
  scheduleTriggers: ScheduleTrigger[];
  broadcastRecipients: Map<string, BroadcastRecipient[]>;
  transcriptions: Map<string, Transcription[]>;
  files: Map<string, File[]>;
  displayArtifacts: Map<string, DisplayArtifact[]>;
} {
  if (!rows || rows.length === 0) {
    return {
      workflowItems: [],
      broadcastActions: [],
      scheduleTriggers: [],
      broadcastRecipients: new Map(),
      transcriptions: new Map(),
      files: new Map(),
      displayArtifacts: new Map(),
    };
  }

  const workflowItemsMap = new Map<string, WorkflowItem>();
  const broadcastActionsMap = new Map<string, BroadcastAction>();
  const scheduleTriggersMap = new Map<string, ScheduleTrigger>();
  const broadcastRecipientsMap = new Map<string, BroadcastRecipient[]>();
  const transcriptionsMap = new Map<string, Transcription[]>();
  const filesMap = new Map<string, File[]>();
  const displayArtifactsMap = new Map<string, DisplayArtifact[]>();

  for (const row of rows) {
    if (row.workflowItem && !workflowItemsMap.has(row.workflowItem.id)) {
      workflowItemsMap.set(row.workflowItem.id, row.workflowItem);
    }
    if (row.broadcastAction && !broadcastActionsMap.has(row.broadcastAction.id)) {
      broadcastActionsMap.set(row.broadcastAction.id, row.broadcastAction);
    }
    if (row.scheduleTrigger && !scheduleTriggersMap.has(row.scheduleTrigger.id)) {
      scheduleTriggersMap.set(row.scheduleTrigger.id, row.scheduleTrigger);
    }

    const contentId = row.workflowItem?.contentId;
    if (contentId) {
      if (row.transcription?.id) {
        const list = transcriptionsMap.get(contentId) || [];
        if (!list.some((t) => t.id === row.transcription!.id)) {
          list.push(row.transcription);
          transcriptionsMap.set(contentId, list);
        }
      }
      if (row.file?.id) {
        const list = filesMap.get(contentId) || [];
        if (!list.some((f) => f.id === row.file!.id)) {
          list.push(row.file);
          filesMap.set(contentId, list);
        }
      }
      if (row.displayArtifact?.id) {
        const list = displayArtifactsMap.get(contentId) || [];
        if (!list.some((d) => d.id === row.displayArtifact!.id)) {
          list.push(row.displayArtifact);
          displayArtifactsMap.set(contentId, list);
        }
      }
    }

    if (row.broadcastRecipient?.id && row.broadcastAction?.broadcastId) {
      const broadcastId = row.broadcastAction.broadcastId;
      const list = broadcastRecipientsMap.get(broadcastId) || [];
      if (!list.some((r) => r.id === row.broadcastRecipient!.id)) {
        list.push(row.broadcastRecipient);
        broadcastRecipientsMap.set(broadcastId, list);
      }
    }
  }

  return {
    workflowItems: Array.from(workflowItemsMap.values()),
    broadcastActions: Array.from(broadcastActionsMap.values()),
    scheduleTriggers: Array.from(scheduleTriggersMap.values()),
    broadcastRecipients: broadcastRecipientsMap,
    transcriptions: transcriptionsMap,
    files: filesMap,
    displayArtifacts: displayArtifactsMap,
  };
}
